/**
 * User Payment History Component
 *
 * Displays payment history table for a user using DataTable.
 */

import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { CreditCard, Loader2, Info } from "lucide-react";
import type { HistoricalTransaction } from "@/services/paymentService";
import type { LaunchpadPaymentStatus } from "@/services/launchpadPaymentService";
import type { User } from "@/services/authService";
import { launchpadPaymentService } from "@/services/launchpadPaymentService";
import { userPaymentColumns } from "../columns/userPaymentColumns";
import { toast } from "sonner";

interface UserPaymentHistoryProps {
  user?: User | null;
  payments: HistoricalTransaction[];
  loading: boolean;
  error: string | null;
  launchpadStatus?: LaunchpadPaymentStatus | null;
  launchpadLoading?: boolean;
}

export function UserPaymentHistory({ user, payments, loading, error, launchpadStatus, launchpadLoading }: UserPaymentHistoryProps) {
  const [payLoading, setPayLoading] = React.useState(false);

  const handleBayarSekarang = async () => {
    if (!user?.callsign) return;

    setPayLoading(true);
    try {
      const result = await launchpadPaymentService.createPayment(user.callsign);
      if (result.redirect_url) {
        window.open(result.redirect_url, "_blank");
      } else {
        toast.error("Tidak dapat membuka halaman pembayaran. Silakan coba lagi.");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Gagal membuat pembayaran";
      toast.error(message);
    } finally {
      setPayLoading(false);
    }
  };

  const columns = React.useMemo(() => userPaymentColumns, []);

  const showPaymentBanner = launchpadStatus?.needPayment ?? false;

  // Launchpad status banner
  const launchpadBanner = (
    <>
      {launchpadLoading && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Memeriksa status pembayaran...
            </div>
          </CardContent>
        </Card>
      )}
      {!launchpadLoading && showPaymentBanner && (
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-amber-900 dark:text-amber-100">
                  Pembayaran Diperlukan
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  Jika Anda sudah melakukan pembayaran, silakan abaikan pesan ini. Proses verifikasi pembayaran mungkin membutuhkan waktu beberapa saat.
                </p>
                {launchpadStatus.amount > 0 && (
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-100 mt-2">
                    Jumlah: {new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR" }).format(launchpadStatus.amount)}
                  </p>
                )}
                <Button
                  className="mt-3"
                  size="sm"
                  onClick={handleBayarSekarang}
                  disabled={payLoading}
                >
                  {payLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Memproses...
                    </>
                  ) : (
                    "Bayar Sekarang"
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );

  if (payments.length === 0 && !loading && !error) {
    return (
      <div className="space-y-4">
        {launchpadBanner}
        <Card>
          <CardHeader>
            <CardTitle>Pembayaran</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-muted-foreground">
              <CreditCard className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>Tidak ada riwayat pembayaran</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {launchpadBanner}
      <Card>
        <CardHeader>
          <CardTitle>Pembayaran</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={payments}
            loading={loading}
            error={error}
            emptyMessage="Tidak ada riwayat pembayaran"
          />
        </CardContent>
      </Card>
    </div>
  );
}
